import { Head, Link, router, usePage } from "@inertiajs/react"
import { toQueryString } from "@/lib/queryParams"
import { useTableDensity } from "@/lib/useTableDensity"
import AddIcon from "@mui/icons-material/Add"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import EditIcon from "@mui/icons-material/Edit"
import EventNoteIcon from "@mui/icons-material/EventNote"
import HistoryIcon from "@mui/icons-material/History"
import PunchClockIcon from "@mui/icons-material/PunchClock"
import RefreshIcon from "@mui/icons-material/Refresh"
import RecordHistoryDrawer from "@/pages/history/RecordHistoryDrawer"
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material"
import dayjs from "dayjs"
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMaterialReactTable,
} from "material-react-table"
import { ElementType, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = Record<string, any>

type SortDirection = "asc" | "desc"

interface IReport {
    id: number
    name: string
    start_date: string | null
    end_date: string | null
    report_date: string | null
    updated_at: string | null
}

function formatDate(val: string | null, withTime = false) {
    if (!val) return "—"
    return dayjs(val).format(withTime ? "DD-MM-YYYY, HH:mm" : "DD-MM-YYYY")
}

type Props = {
    reports: IReport[]
    search: string
    sort: string
    direction: SortDirection
    pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
}

function DeleteDialog({ report, onClose, tableParams }: {
    report: IReport | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IReport | null>(null)

    const handleDelete = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.delete(`/weekly_reports/${snapshot.id}?${query}`, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog open={report !== null} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(report) } }}>
            <DialogTitle>{t("weeklyReports.deleteTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t("weeklyReports.deleteText", { name: snapshot?.name })}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing}>{t("common.cancel")}</Button>
                <Button color="error" variant="contained" onClick={handleDelete} disabled={processing}>
                    {processing ? t("common.deleting") : t("common.delete")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function DuplicateDialog({ report, onClose, tableParams }: {
    report: IReport | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IReport | null>(null)

    const handleDuplicate = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.post(`/weekly_reports/${snapshot.id}/duplicate?${query}`, {}, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog open={report !== null} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(report) } }}>
            <DialogTitle>{t("weeklyReports.duplicateTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t("weeklyReports.duplicateText", { name: snapshot?.name })}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing}>{t("common.cancel")}</Button>
                <Button variant="contained" onClick={handleDuplicate} disabled={processing}>
                    {processing ? t("weeklyReports.duplicating") : t("common.duplicate")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function WeeklyReportsIndex() {
    const { reports, search: initialSearch, sort, direction, pagination } = usePage<Props>().props
    const { t } = useTranslation()

    const [deleteReport, setDeleteReport] = useState<IReport | null>(null)
    const [duplicateReport, setDuplicateReport] = useState<IReport | null>(null)
    const [historyTarget, setHistoryTarget] = useState<{ type: string; id: number } | null>(null)
    const [reloading, setReloading] = useState(false)
    const [timeCardLoading, setTimeCardLoading] = useState(false)
    const { density, onDensityChange } = useTableDensity("weekly_reports")
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const timeCardInputRef = useRef<HTMLInputElement>(null)

    const [sorting, setSorting] = useState<MRT_SortingState>(
        sort ? [{ id: sort, desc: direction === "desc" }] : []
    )
    const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
        pageIndex: pagination.current_page - 1,
        pageSize: pagination.per_page,
    })
    const [globalFilter, setGlobalFilter] = useState(initialSearch ?? "")

    useEffect(() => { setGlobalFilter(initialSearch ?? "") }, [initialSearch])
    useEffect(() => { setSorting(sort ? [{ id: sort, desc: direction === "desc" }] : []) }, [sort, direction])
    useEffect(() => {
        setPaginationState({ pageIndex: pagination.current_page - 1, pageSize: pagination.per_page })
    }, [pagination.current_page, pagination.per_page])

    useEffect(() => {
        const key = "scroll_weekly_reports"
        const saved = sessionStorage.getItem(key)
        if (saved) {
            sessionStorage.removeItem(key)
            requestAnimationFrame(() => { window.scrollTo({ top: parseInt(saved) }) })
        }
        return router.on("before", () => { sessionStorage.setItem(key, String(window.scrollY)) })
    }, [])

    const visit = useCallback((params: Record<string, string | number>) => {
        router.get("/weekly_reports", params, { preserveState: true, preserveScroll: true })
    }, [])

    const tableParams = useMemo(() => ({
        search: globalFilter,
        sort: sorting[0]?.id ?? "",
        direction: (sorting[0]?.desc ? "desc" : "asc") as SortDirection,
        page: paginationState.pageIndex + 1,
        per_page: paginationState.pageSize,
    }), [globalFilter, sorting, paginationState])

    const newUrl = useMemo(() =>
        `/weekly_reports/new?${toQueryString(tableParams)}`
    , [tableParams])

    const handleTimeCardFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setTimeCardLoading(true)
        const formData = new FormData()
        formData.append("file", file)
        router.post("/weekly_reports/parse_time_card", formData as AnyData, {
            onFinish: () => { setTimeCardLoading(false); e.target.value = "" },
        })
    }

    const columns = useMemo<MRT_ColumnDef<IReport>[]>(() => [
        { accessorKey: "name", header: t("common.name"), size: 300 },
        { accessorKey: "start_date", header: t("common.startDate"), size: 130, Cell: ({ cell }) => formatDate(cell.getValue<string | null>()) },
        { accessorKey: "end_date", header: t("common.endDate"), size: 130, Cell: ({ cell }) => formatDate(cell.getValue<string | null>()) },
        { accessorKey: "report_date", header: t("weeklyReports.reportDate"), size: 130, Cell: ({ cell }) => formatDate(cell.getValue<string | null>()) },
        { accessorKey: "updated_at", header: t("common.updatedAt"), size: 190, Cell: ({ cell }) => formatDate(cell.getValue<string | null>(), true) },
    ], [t])

    const table = useMaterialReactTable({
        columns,
        data: reports,
        enableRowActions: true,
        positionActionsColumn: "last",
        enableColumnFilters: false,
        enableFullScreenToggle: false,
        initialState: { showGlobalFilter: !!initialSearch },
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount: pagination.total_count,
        onGlobalFilterChange: (updater) => {
            const val = typeof updater === "function" ? updater(globalFilter) : (updater ?? "")
            setGlobalFilter(val)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                visit({
                    search: val,
                    sort: sorting[0]?.id ?? "",
                    direction: sorting[0]?.desc ? "desc" : "asc",
                    page: 1,
                    per_page: paginationState.pageSize,
                })
            }, 400)
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater(paginationState) : updater
            setPaginationState(next)
            visit({
                search: globalFilter,
                sort: sorting[0]?.id ?? "",
                direction: sorting[0]?.desc ? "desc" : "asc",
                page: next.pageIndex + 1,
                per_page: next.pageSize,
            })
        },
        onSortingChange: (updater) => {
            const next = typeof updater === "function" ? updater(sorting) : updater
            setSorting(next)
            visit({
                search: globalFilter,
                sort: next[0]?.id ?? "",
                direction: next[0]?.desc ? "desc" : "asc",
                page: 1,
                per_page: paginationState.pageSize,
            })
        },
        muiPaginationProps: {
            rowsPerPageOptions: [25, 50, 100, 200],
            showFirstButton: true,
            showLastButton: true,
        },
        renderRowActions: ({ row }) => {
            const editUrl = `/weekly_reports/${row.original.id}/edit?${toQueryString(tableParams)}`
            return (
                <Box sx={{ display: "flex" }}>
                    <Tooltip title={t("common.edit")}>
                        <IconButton color="info" size="small" component={Link as ElementType} href={editUrl}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.downloadExcel")}>
                        <IconButton color="success" size="small" onClick={() => { window.location.href = `/weekly_reports/${row.original.id}/download_excel` }}>
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.duplicate")}>
                        <IconButton color="warning" size="small" onClick={() => setDuplicateReport(row.original)}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("history.openRecordHistory")}>
                        <IconButton size="small" onClick={() => setHistoryTarget({ type: "Report", id: row.original.id })}>
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                        <IconButton color="error" size="small" onClick={() => setDeleteReport(row.original)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
        onDensityChange,
        state: {
            showProgressBars: reloading,
            pagination: paginationState,
            globalFilter,
            sorting,
            density,
        },
    })

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Head title={t("weeklyReports.title")} />

            <Box sx={{ mb: 2 }}>
                <Button LinkComponent={Link} href="/" size="small" startIcon={<ArrowBackIcon />} color="inherit">
                    {t("common.home")}
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("weeklyReports.title")}</Typography>
                    <Tooltip title={t("common.reload")}>
                        <IconButton size="small" onClick={() => { setReloading(true); router.reload({ onFinish: () => setReloading(false) }) }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <input ref={timeCardInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleTimeCardFile} />
                    <Tooltip title={timeCardLoading ? t("common.loading") : t("weeklyReports.parseTimecard")}>
                        <span>
                            <Button variant="outlined" color="success" startIcon={<PunchClockIcon />}
                                onClick={() => timeCardInputRef.current?.click()} disabled={timeCardLoading} size="small">
                                {timeCardLoading ? t("common.loading") : t("weeklyReports.parseTimecard")}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button variant="outlined" color="info" startIcon={<EventNoteIcon />} LinkComponent={Link}
                        href="/weekly_reports/monthly_log" size="small">
                        {t("weeklyReports.monthlyLog")}
                    </Button>
                    <Button
                        LinkComponent={Link}
                        href="/history?module=weekly_reports"
                        startIcon={<HistoryIcon />}
                        variant="outlined"
                        size="small"
                    >
                        {t("history.openHistory")}
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} LinkComponent={Link} href={newUrl} size="small">
                        {t("weeklyReports.new")}
                    </Button>
                </Box>
            </Box>

            <MaterialReactTable table={table} />

            <DeleteDialog report={deleteReport} onClose={() => setDeleteReport(null)} tableParams={tableParams} />
            <DuplicateDialog report={duplicateReport} onClose={() => setDuplicateReport(null)} tableParams={tableParams} />
            <RecordHistoryDrawer
                open={historyTarget !== null}
                onClose={() => setHistoryTarget(null)}
                type={historyTarget?.type ?? null}
                id={historyTarget?.id ?? null}
            />
        </Container>
    )
}
